/**
 * Which TABLE each seating unit goes to. (Which seat is autoSeatSeats.ts.)
 *
 * A "unit" arrives from lib/autoSeat.ts already formed: one party (a guest and
 * their plus-one chairs) or parties joined by a must-together link. Units are
 * never split. This module decides where they go, in this precedence:
 *
 *  2. HOUSEHOLD — members of one household tag sit at ONE table when any table
 *     can hold them; otherwise they are spread over the fewest ADJACENT tables
 *     and the plan warns.
 *  3. COHORT (the host's relationship group) — each group is placed as a block at
 *     one table. A group bigger than any table spills into adjacent tables, split
 *     evenly, never scattered. Small groups pack into a table together before a
 *     new one is opened. A guest with several group tags uses the first in the
 *     host's tag list (lib/seatingGroups.ts). In "Mix groups" mode the same
 *     cohort is instead spread round-robin, still keeping households whole.
 *  4. BALANCE — every table is given a soft target (the room's overall fill) and
 *     a block goes where it stays under it, so 30 guests at five tables of eight
 *     is six a table, not 8/8/8/6/0. A group that cannot fit under the target
 *     still sits together: groups beat balance. If the room is clearly surplus
 *     (twice the seats it needs) only the first tables are used and the rest stay
 *     empty at the END, rather than a thin scatter over the whole room.
 *  5. UNTAGGED guests fill what is left, preferring a table where a prefer-link
 *     partner sits, then a seeded-random table under the target.
 *  6. RANDOMNESS — the order blocks are placed in, and every tie, come from the
 *     seeded PRNG, never from a name. Since a random order sometimes packs badly,
 *     the whole placement is redrawn ATTEMPTS times from the same seeded stream
 *     and the best arrangement wins (fewest unseated, then split households,
 *     then scattered cohorts, then uneven tables, then unmet prefer-links).
 *     Still a pure function of (seed, input).
 *
 * Rule 1 — the hard rules (capacity, parties whole, must-together, must-apart)
 * sits under all of it: every candidate table is filtered by them first, and no
 * preference above can override one.
 */
import { assignSeats } from './autoSeatSeats';
import { groupLabels, type GuestGroups, type GroupTag } from './seatingGroups';
import type { SeatPattern } from './seatRelation';
import { createRandom, type Random } from './seededRandom';
import type {
  BondIndex,
  ChairSpec,
  Party,
  Placed,
  PlannedChair,
  UnseatedCode,
  UnseatedGuest,
  Unit,
} from './autoSeatTypes';
import type { SeatingTable } from '../types/seating';

export type SeatGrouping = 'by_relationship' | 'mix_groups';

/** How many times the placement is redrawn (all from the one seeded stream). */
const ATTEMPTS = 32;
const HOUSEHOLD_WEIGHT = 1000;

export interface EngineBaseline {
  /** Chairs already held per table: kept, blocked and locked. */
  taken: Map<string, Set<number>>;
  peopleAt: Map<string, Set<string>>;
  tableOf: Map<string, string>;
}

export interface EngineInput {
  /** In room order. */
  tables: SeatingTable[];
  /** In a canonical (id-based) order; the engine shuffles from there. */
  units: Unit[];
  bonds: BondIndex;
  baseline: EngineBaseline;
  groups: ReadonlyMap<string, GuestGroups>;
  grouping: SeatGrouping;
  pattern: SeatPattern;
  seed: number;
  names: ReadonlyMap<string, string>;
  previousChair: (party: Party) => { tableLabel: string; seatIndex: number } | null;
}

export interface EngineResult {
  placements: PlannedChair[];
  unseated: UnseatedGuest[];
  warnings: string[];
  tableOf: Map<string, string>;
}

interface Sub {
  units: Unit[];
  chairs: number;
  household: GroupTag | null;
}
interface Block {
  cohort: GroupTag | null;
  subs: Sub[];
  chairs: number;
  households: Set<string>;
}
interface Run {
  added: Map<string, number>;
  peopleAt: Map<string, Set<string>>;
  tableOf: Map<string, string>;
  placed: Placed[];
  unseated: UnseatedGuest[];
}
interface Anchor {
  households: ReadonlySet<string>;
  cohort: string | null;
}

const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
const NO_ANCHOR: Anchor = { households: new Set(), cohort: null };

/** Lexicographic: is `a` strictly smaller than `b`? */
function smaller(a: number[], b: number[]): boolean {
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return a[i] < b[i];
  return false;
}

export function placeUnits(input: EngineInput): EngineResult {
  const { tables, baseline, groups, bonds } = input;
  const maxCapacity = tables.reduce((max, table) => Math.max(max, table.capacity), 0);
  const tableIndex = new Map(tables.map((table, index) => [table.id, index]));
  const tableById = new Map(tables.map((table) => [table.id, table]));
  const idsOf = new Map<Unit, string[]>();
  const idsFor = (unit: Unit) => {
    if (!idsOf.has(unit)) {
      idsOf.set(unit, unit.parties.map((party) => party.candidate.inviteeId).filter(Boolean) as string[]);
    }
    return idsOf.get(unit)!;
  };
  const partners = (map: Map<string, Set<string>>, id: string) => map.get(id) ?? new Set<string>();

  // ---- what each unit belongs to ----
  const infoOf = (unit: Unit): GuestGroups => {
    let household: GroupTag | null = null;
    let cohort: GroupTag | null = null;
    for (const id of idsFor(unit)) {
      const entry = groups.get(id);
      household = household ?? entry?.household ?? null;
      cohort = cohort ?? entry?.cohort ?? null;
    }
    return { household, cohort };
  };

  const live = input.units.filter((unit) => unit.conflict || unit.chairs > 0);
  const fixed = live.filter((unit) => unit.conflict || unit.pins.size > 0);
  const free = live.filter((unit) => !unit.conflict && unit.pins.size === 0);
  const newChairs = live.reduce((sum, unit) => sum + (unit.conflict ? 0 : unit.chairs), 0);

  // ---- soft targets: which tables are in play, and how full each should be ----
  const baseFree = (table: SeatingTable) => table.capacity - baseline.taken.get(table.id)!.size;
  const totalFree = tables.reduce((sum, table) => sum + baseFree(table), 0);
  const pinTables = new Set(fixed.flatMap((unit) => [...unit.pins]));
  let active = new Set(tables.map((table) => table.id));
  if (input.grouping === 'by_relationship' && newChairs > 0 && totalFree >= 2 * newChairs) {
    // Tables somebody already sits at come first (fill them before opening a new
    // one), then the room in order until there are enough chairs.
    const inPlay = tables.filter((table) => pinTables.has(table.id) || baseline.taken.get(table.id)!.size > 0);
    active = new Set(inPlay.map((table) => table.id));
    let cumulative = inPlay.reduce((sum, table) => sum + baseFree(table), 0);
    for (const table of tables) {
      if (cumulative >= newChairs) break;
      if (active.has(table.id)) continue;
      active.add(table.id);
      cumulative += baseFree(table);
    }
  }
  const activeTables = tables.filter((table) => active.has(table.id));
  const activeCapacity = activeTables.reduce((sum, table) => sum + table.capacity, 0);
  const activeTaken = activeTables.reduce((sum, table) => sum + baseline.taken.get(table.id)!.size, 0);
  const ratio = activeCapacity > 0 ? Math.min(1, (activeTaken + newChairs) / activeCapacity) : 1;
  const softCap = (table: SeatingTable) =>
    active.has(table.id) ? Math.min(table.capacity, Math.ceil(ratio * table.capacity - 1e-9)) : table.capacity;

  // ---- blocks: households and cohorts, then prefer-link clusters, then loose units ----
  const households = new Map<string, Unit[]>();
  const looseWithCohort: Unit[] = [];
  const untagged: Unit[] = [];
  for (const unit of free) {
    const info = infoOf(unit);
    if (info.household) households.set(info.household.id, [...(households.get(info.household.id) ?? []), unit]);
    else if (info.cohort) looseWithCohort.push(unit);
    else untagged.push(unit);
  }
  const cohortBlocks = new Map<string, Block>();
  const blocks: Block[] = [];
  const addToCohort = (cohort: GroupTag, sub: Sub) => {
    let block = cohortBlocks.get(cohort.id);
    if (!block) {
      block = { cohort, subs: [], chairs: 0, households: new Set() };
      cohortBlocks.set(cohort.id, block);
      blocks.push(block);
    }
    block.subs.push(sub);
    block.chairs += sub.chairs;
    if (sub.household) block.households.add(sub.household.id);
  };
  for (const [, units] of households) {
    const household = infoOf(units[0]).household!;
    const sub: Sub = { units, chairs: units.reduce((sum, unit) => sum + unit.chairs, 0), household };
    // A household outranks a cohort: it goes with the cohort most of its units share
    // (ties: the one the host listed first), so its members are never pulled apart.
    const votes = new Map<string, { tag: GroupTag; count: number }>();
    for (const unit of units) {
      const tag = infoOf(unit).cohort;
      if (tag) votes.set(tag.id, { tag, count: (votes.get(tag.id)?.count ?? 0) + 1 });
    }
    const winner = [...votes.values()].sort(
      (a, b) => b.count - a.count || a.tag.order - b.tag.order || cmp(a.tag.id, b.tag.id),
    )[0];
    if (winner) addToCohort(winner.tag, sub);
    else blocks.push({ cohort: null, subs: [sub], chairs: sub.chairs, households: new Set([household.id]) });
  }
  for (const unit of looseWithCohort) {
    addToCohort(infoOf(unit).cohort!, { units: [unit], chairs: unit.chairs, household: null });
  }

  const unitOf = new Map<string, Unit>();
  for (const unit of untagged) for (const id of idsFor(unit)) unitOf.set(id, unit);
  const parent = new Map<Unit, Unit>(untagged.map((unit) => [unit, unit]));
  const root = (unit: Unit): Unit => {
    let at = unit;
    while (parent.get(at) !== at) at = parent.get(at)!;
    return at;
  };
  for (const unit of untagged) {
    for (const id of idsFor(unit)) {
      for (const other of partners(bonds.softTogether, id)) {
        const mate = unitOf.get(other);
        if (mate && mate !== unit) parent.set(root(mate), root(unit));
      }
    }
  }
  const clusterMap = new Map<Unit, Unit[]>();
  for (const unit of untagged) clusterMap.set(root(unit), [...(clusterMap.get(root(unit)) ?? []), unit]);
  const clusters = [...clusterMap.values()].filter((group) => group.length > 1);
  const loose = [...clusterMap.values()].filter((group) => group.length === 1).map((group) => group[0]);

  // ---- household membership, for scoring ----
  const householdMembers = new Map<string, { label: string; ids: string[] }>();
  for (const [id, entry] of [...groups.entries()].sort((a, b) => cmp(a[0], b[0]))) {
    if (!entry.household) continue;
    const found = householdMembers.get(entry.household.id) ?? { label: entry.household.label, ids: [] };
    found.ids.push(id);
    householdMembers.set(entry.household.id, found);
  }

  const cohortOfInvitee = (id: string | null | undefined) => (id ? groups.get(id)?.cohort ?? null : null);

  // ================= one attempt =================
  let run!: Run;
  const fresh = (): Run => ({
    added: new Map(),
    peopleAt: new Map([...baseline.peopleAt].map(([id, set]) => [id, new Set(set)])),
    tableOf: new Map(baseline.tableOf),
    placed: [],
    unseated: [],
  });

  const room = (table: SeatingTable) =>
    table.capacity - baseline.taken.get(table.id)!.size - (run.added.get(table.id) ?? 0);
  const softRoom = (table: SeatingTable) =>
    softCap(table) - baseline.taken.get(table.id)!.size - (run.added.get(table.id) ?? 0);

  const apartOk = (unit: Unit, tableId: string) =>
    idsFor(unit).every((id) =>
      [...partners(bonds.mustApart, id)].every((other) => !run.peopleAt.get(tableId)!.has(other)),
    );
  const pairwiseApartOk = (units: Unit[]) => {
    const all = units.flatMap(idsFor);
    return all.every((id) => [...partners(bonds.mustApart, id)].every((other) => !all.includes(other)));
  };
  const bondScore = (units: Unit[], tableId: string) => {
    let total = 0;
    for (const unit of units) {
      for (const id of idsFor(unit)) {
        for (const other of partners(bonds.softTogether, id)) if (run.peopleAt.get(tableId)!.has(other)) total += 1;
        for (const other of partners(bonds.softApart, id)) if (run.peopleAt.get(tableId)!.has(other)) total -= 1;
      }
    }
    return total;
  };
  const anchorScore = (tableId: string, anchor: Anchor) => {
    let total = 0;
    for (const id of run.peopleAt.get(tableId)!) {
      const entry = groups.get(id);
      if (entry?.household && anchor.households.has(entry.household.id)) total += HOUSEHOLD_WEIGHT;
      if (anchor.cohort && entry?.cohort?.id === anchor.cohort) total += 1;
    }
    return total;
  };
  const anchorOfUnit = (unit: Unit): Anchor => {
    const info = infoOf(unit);
    return { households: new Set(info.household ? [info.household.id] : []), cohort: info.cohort?.id ?? null };
  };
  const anchorOfBlock = (block: Block): Anchor => ({ households: block.households, cohort: block.cohort?.id ?? null });

  /** The item with the LARGEST key (lexicographic); exact ties are broken by the seeded PRNG. */
  const pickBest = <T,>(items: T[], keyOf: (item: T) => number[]): T | null => {
    if (items.length === 0) return null;
    const keyed = items.map((item) => ({ item, key: keyOf(item) }));
    let top = keyed[0].key;
    for (const entry of keyed) if (smaller(top, entry.key)) top = entry.key;
    const ties = keyed.filter((entry) => !smaller(top, entry.key) && !smaller(entry.key, top));
    return (ties.length === 1 ? ties[0] : ties[Math.floor(rng.next() * ties.length)]).item;
  };

  type Rank = (table: SeatingTable, need: number, phase: number) => number[];
  /**
   * The table for units that must sit together, or null. Three tiers, each
   * loosening one preference but never a hard rule: under the soft target in a
   * table in play; any table in play with the chairs; any table with the chairs.
   */
  const chooseTable = (units: Unit[], need: number, anchor: Anchor, packing: boolean, rank?: Rank) => {
    const tiers: Array<(table: SeatingTable) => boolean> = [
      (table) => active.has(table.id) && softRoom(table) >= need && room(table) >= need,
      (table) => active.has(table.id) && room(table) >= need,
      (table) => room(table) >= need,
    ];
    // A 'prefer' keep-apart is a preference, but one worth a looser tier: try every
    // tier without breaking one before any tier that does.
    for (const strict of [true, false]) {
      for (const [tier, allowed] of tiers.entries()) {
        const pool = tables.filter(
          (table) =>
            allowed(table) &&
            units.every((unit) => apartOk(unit, table.id)) &&
            (!strict || bondScore(units, table.id) >= 0),
        );
        if (pool.length === 0) continue;
        return pickBest(pool, (table) =>
          rank
            ? rank(table, need, tier)
            : [
                anchorScore(table.id, anchor),
                bondScore(units, table.id),
                // Under the target: pack tightly (best fit). Over it: take the emptiest table.
                tier === 0 ? (packing ? -(softRoom(table) - need) : 0) : room(table) - need,
              ],
        );
      }
    }
    return null;
  };

  const previousOf = input.previousChair;
  /** Puts as many of the unit's chairs as fit at `table`; returns the chairs left over, per party. */
  const seatAt = (unit: Unit, table: SeatingTable): Map<Party, number> => {
    const left = new Map<Party, number>();
    const flat: Array<{ party: Party; spec: ChairSpec }> = unit.parties.flatMap((party) =>
      party.chairs.map((spec) => ({ party, spec })),
    );
    let space = room(table);
    for (const { party, spec } of flat) {
      if (space <= 0) {
        left.set(party, (left.get(party) ?? 0) + 1);
        continue;
      }
      space -= 1;
      run.added.set(table.id, (run.added.get(table.id) ?? 0) + 1);
      run.placed.push({ table, unit, party, spec });
      if (spec.inviteeId) {
        run.tableOf.set(spec.inviteeId, table.id);
        run.peopleAt.get(table.id)!.add(spec.inviteeId);
      }
    }
    return left;
  };

  const report = (party: Party, seats: number, code: UnseatedCode, reason: string) =>
    run.unseated.push({
      name: party.candidate.displayName.trim() || 'Guest',
      inviteeId: party.candidate.inviteeId ?? null,
      seats,
      code,
      reason,
    });

  const placeUnit = (unit: Unit) => {
    if (unit.conflict) {
      for (const party of unit.parties) report(party, party.chairs.length, 'rules_conflict', unit.conflict);
      return;
    }
    if (unit.pins.size === 1) {
      const table = tableById.get([...unit.pins][0])!;
      if (unit.parties.every((party) => party.pin === table.id) || apartOk(unit, table.id)) {
        for (const [party, seats] of seatAt(unit, table)) {
          report(party, seats, 'pinned_table_full', `${table.label} has no chair left for ${seats === 1 ? 'them' : `${seats} more`}, and their party stays together.`);
        }
      } else {
        for (const party of unit.parties) {
          report(party, party.chairs.length, 'keep_apart', `The table they belong at (${table.label}) seats someone they must be kept apart from.`);
        }
      }
      return;
    }
    const table = chooseTable([unit], unit.chairs, anchorOfUnit(unit), false);
    if (table) {
      seatAt(unit, table);
      return;
    }
    const fits = tables.filter((candidate) => room(candidate) >= unit.chairs);
    for (const party of unit.parties) {
      const seats = party.chairs.length;
      if (unit.chairs > maxCapacity) {
        report(party, seats, 'party_too_big', `Their party of ${unit.chairs} is bigger than the largest table (${maxCapacity} chairs), and a party is never split.`);
      } else if (fits.length > 0) {
        report(party, seats, 'keep_apart', 'Every table with room seats someone they must be kept apart from.');
      } else {
        report(party, seats, 'no_room', unit.chairs === 1 ? 'Every table is full.' : `No table has ${unit.chairs} free chairs together, and their party is never split.`);
      }
    }
  };

  // ---- blocks ----
  let rng!: Random;
  const seatSub = (sub: Sub, table: SeatingTable) => sub.units.forEach((unit) => seatAt(unit, table));

  const placeSubIn = (sub: Sub, window: SeatingTable[], quota: Map<string, number>) => {
    const fitsWhole = (table: SeatingTable) =>
      room(table) >= sub.chairs && sub.units.every((unit) => apartOk(unit, table.id));
    if (pairwiseApartOk(sub.units)) {
      const inQuota = window.find((table) => (quota.get(table.id) ?? 0) >= sub.chairs && fitsWhole(table));
      const anywhere = window.filter(fitsWhole).sort((a, b) => room(b) - room(a))[0];
      const table = inQuota ?? anywhere;
      if (table) {
        seatSub(sub, table);
        quota.set(table.id, (quota.get(table.id) ?? 0) - sub.chairs);
        return;
      }
    }
    // Too big for one table of the run (a big household): keep each PARTY whole, spread over the fewest tables.
    for (const unit of [...sub.units].sort((a, b) => b.chairs - a.chairs)) {
      const table =
        window.find((candidate) => (quota.get(candidate.id) ?? 0) >= unit.chairs && room(candidate) >= unit.chairs && apartOk(unit, candidate.id)) ??
        window.find((candidate) => room(candidate) >= unit.chairs && apartOk(unit, candidate.id));
      if (table) {
        seatAt(unit, table);
        quota.set(table.id, (quota.get(table.id) ?? 0) - unit.chairs);
      } else {
        placeUnit(unit);
      }
    }
  };

  /** A block too big for any one table: the fewest ADJACENT tables, filled evenly. */
  const spill = (block: Block) => {
    const windows: SeatingTable[][] = [];
    for (let start = 0; start < tables.length; start += 1) {
      let sum = 0;
      for (let end = start; end < tables.length; end += 1) {
        sum += room(tables[end]);
        if (sum >= block.chairs) {
          if (room(tables[end]) > 0 && room(tables[start]) > 0) windows.push(tables.slice(start, end + 1));
          break;
        }
      }
    }
    const anchor = anchorOfBlock(block);
    const window = pickBest(windows, (candidate) => [
      -candidate.length,
      -candidate.filter((table) => !active.has(table.id)).length,
      candidate.reduce((sum, table) => sum + anchorScore(table.id, anchor), 0),
    ]);
    if (!window) {
      for (const sub of block.subs) for (const unit of sub.units) placeUnit(unit);
      return;
    }
    const target = Math.ceil(block.chairs / window.length);
    const quota = new Map(window.map((table) => [table.id, Math.min(room(table), target)]));
    let short = block.chairs - [...quota.values()].reduce((sum, n) => sum + n, 0);
    for (const table of window) {
      const extra = Math.min(short, room(table) - quota.get(table.id)!);
      if (extra > 0) {
        quota.set(table.id, quota.get(table.id)! + extra);
        short -= extra;
      }
    }
    // Whole households first, biggest first, so the ones that could be split are placed last.
    const subs = rng.shuffle(block.subs).sort((a, b) => b.chairs - a.chairs);
    for (const sub of subs) placeSubIn(sub, window, quota);
  };

  const placeBlock = (block: Block) => {
    const units = block.subs.flatMap((sub) => sub.units);
    if (block.chairs <= maxCapacity && pairwiseApartOk(units)) {
      const table = chooseTable(units, block.chairs, anchorOfBlock(block), true);
      if (table) {
        units.forEach((unit) => seatAt(unit, table));
        return;
      }
    }
    spill(block);
  };

  /** Mix mode: spread one cohort across the tables, a household at a time. */
  const placeMix = (block: Block) => {
    const cohortId = block.cohort!.id;
    const cohortAt = (tableId: string) =>
      [...run.peopleAt.get(tableId)!].filter((id) => groups.get(id)?.cohort?.id === cohortId).length;
    for (const sub of rng.shuffle(block.subs)) {
      const rank: Rank = (table, need) => [-cohortAt(table.id), bondScore(sub.units, table.id), room(table) - need];
      const table = pairwiseApartOk(sub.units)
        ? chooseTable(sub.units, sub.chairs, NO_ANCHOR, false, rank)
        : null;
      if (table) seatSub(sub, table);
      else for (const unit of sub.units) placeUnit(unit);
    }
  };

  const attempt = (): Run => {
    run = fresh();
    for (const unit of fixed) placeUnit(unit);
    for (const block of rng.shuffle(blocks)) {
      if (input.grouping === 'mix_groups' && block.cohort) placeMix(block);
      else placeBlock(block);
    }
    for (const cluster of rng.shuffle(clusters)) {
      const chairs = cluster.reduce((sum, unit) => sum + unit.chairs, 0);
      const table = pairwiseApartOk(cluster) ? chooseTable(cluster, chairs, NO_ANCHOR, true) : null;
      if (table) cluster.forEach((unit) => seatAt(unit, table));
      else for (const unit of rng.shuffle(cluster)) placeUnit(unit);
    }
    for (const unit of rng.shuffle(loose)) placeUnit(unit);
    return run;
  };

  // ================= judging attempts =================
  const baselineCohorts = new Map<string, Map<string, number>>();
  for (const [tableId, people] of baseline.peopleAt) {
    for (const id of people) {
      const cohort = cohortOfInvitee(id);
      if (!cohort) continue;
      const perTable = baselineCohorts.get(cohort.id) ?? new Map<string, number>();
      perTable.set(tableId, (perTable.get(tableId) ?? 0) + 1);
      baselineCohorts.set(cohort.id, perTable);
    }
  }
  const cohortSpread = (attemptRun: Run) => {
    const perCohort = new Map<string, Map<string, number>>(
      [...baselineCohorts].map(([id, perTable]) => [id, new Map(perTable)]),
    );
    for (const chair of attemptRun.placed) {
      const cohort = cohortOfInvitee(chair.party.candidate.inviteeId);
      if (!cohort) continue;
      const perTable = perCohort.get(cohort.id) ?? new Map<string, number>();
      perTable.set(chair.table.id, (perTable.get(chair.table.id) ?? 0) + 1);
      perCohort.set(cohort.id, perTable);
    }
    return perCohort;
  };
  const softMisses = (attemptRun: Run): string[] => {
    const touched = new Set(attemptRun.placed.map((chair) => chair.spec.inviteeId).filter(Boolean) as string[]);
    const out: string[] = [];
    const seen = new Set<string>();
    const nameOf = (id: string) => input.names.get(id);
    const walk = (map: Map<string, Set<string>>, misses: (a: string | undefined, b: string | undefined) => string | null) => {
      for (const [id, others] of map) {
        for (const other of others) {
          const pair = cmp(id, other) <= 0 ? `${id}|${other}` : `${other}|${id}`;
          if (seen.has(pair) || !(touched.has(id) || touched.has(other))) continue;
          seen.add(pair);
          const note = misses(attemptRun.tableOf.get(id), attemptRun.tableOf.get(other));
          if (note) out.push(note.replace('{a}', nameOf(id) ?? 'A guest').replace('{b}', nameOf(other) ?? 'a guest'));
        }
      }
    };
    walk(bonds.softTogether, (a, b) =>
      a && b && a !== b ? '{a} and {b} would like to sit together but ended up at different tables.' : null);
    walk(bonds.softApart, (a, b) =>
      a && a === b ? '{a} and {b} would rather not share a table but do.' : null);
    return out;
  };
  const householdSplit = (attemptRun: Run) => {
    let splits = 0;
    for (const { ids } of householdMembers.values()) {
      const at = new Set(ids.map((id) => attemptRun.tableOf.get(id)).filter(Boolean));
      splits += Math.max(0, at.size - 1);
    }
    return splits;
  };
  const judge = (attemptRun: Run): number[] => {
    const unseated = attemptRun.unseated.reduce((sum, entry) => sum + entry.seats, 0);
    let cohorts = 0;
    for (const perTable of cohortSpread(attemptRun).values()) {
      const total = [...perTable.values()].reduce((sum, n) => sum + n, 0);
      const used = perTable.size;
      if (input.grouping === 'mix_groups') {
        cohorts += Math.max(0, Math.min(total, activeTables.length) - used);
      } else {
        const indexes = [...perTable.keys()].map((id) => tableIndex.get(id)!);
        const span = Math.max(...indexes) - Math.min(...indexes) + 1;
        cohorts += Math.max(0, used - Math.ceil(total / Math.max(maxCapacity, 1))) + (span - used);
      }
    }
    const deviations = activeTables.map(
      (table) => baseline.taken.get(table.id)!.size + (attemptRun.added.get(table.id) ?? 0) - ratio * table.capacity,
    );
    const spread = deviations.length > 0 ? Math.max(...deviations) - Math.min(...deviations) : 0;
    return [unseated, householdSplit(attemptRun), cohorts, Math.round(spread * 100), softMisses(attemptRun).length];
  };
  const perfect = (score: number[]) =>
    score[0] === 0 && score[1] === 0 && score[2] === 0 && score[3] <= 100 && score[4] === 0;

  // ================= go =================
  rng = createRandom(input.seed);
  let best: Run | null = null;
  let bestScore: number[] = [];
  for (let i = 0; i < ATTEMPTS; i += 1) {
    const candidate = attempt();
    const score = judge(candidate);
    if (!best || smaller(score, bestScore)) {
      best = candidate;
      bestScore = score;
    }
    if (perfect(score)) break;
  }
  const winner = best!;

  // ---- seats, then the plan's words ----
  const { seatOf, warnings: seatWarnings } = assignSeats({
    tables,
    placed: winner.placed,
    taken: baseline.taken,
    pattern: input.pattern,
    random: createRandom((input.seed ^ 0x9e3779b9) >>> 0),
  });
  const placements: PlannedChair[] = winner.placed
    .map((chair) => {
      const inviteeId = chair.party.candidate.inviteeId ?? null;
      return {
        tableId: chair.table.id,
        tableLabel: chair.table.label,
        seatIndex: seatOf.get(chair)!,
        ...chair.spec,
        from: chair.spec.companion ? null : previousOf(chair.party),
        ...groupLabels(inviteeId ? groups.get(inviteeId) : undefined),
        locked: false,
      };
    })
    .sort((a, b) => tableIndex.get(a.tableId)! - tableIndex.get(b.tableId)! || a.seatIndex - b.seatIndex);

  const warnings = [...softMisses(winner)];
  const touched = new Set(winner.placed.map((chair) => chair.spec.inviteeId).filter(Boolean) as string[]);
  for (const { label, ids } of householdMembers.values()) {
    if (!ids.some((id) => touched.has(id))) continue;
    const at = new Set(ids.map((id) => winner.tableOf.get(id)).filter(Boolean));
    if (at.size > 1) {
      warnings.push(`The ${label} household couldn’t all fit at one table, so they are split over ${at.size} tables.`);
    }
  }
  if (input.grouping === 'by_relationship') {
    for (const block of cohortBlocks.values()) {
      const perTable = cohortSpread(winner).get(block.cohort!.id);
      const total = perTable ? [...perTable.values()].reduce((sum, n) => sum + n, 0) : 0;
      if (perTable && perTable.size > 1 && total <= maxCapacity) {
        warnings.push(`“${block.cohort!.label}” couldn’t all sit at one table, so they are spread over ${perTable.size}.`);
      }
    }
  }
  warnings.push(...seatWarnings);

  return {
    placements,
    unseated: winner.unseated,
    warnings: warnings.sort(cmp),
    tableOf: winner.tableOf,
  };
}
